/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import WebsiteNavbar from "../components/WebsiteNavbar";
import Footer from "../components/Footer";
import InvoiceModal from "../components/InvoiceModal";
import api from "../services/api";
import {
  isLoggedIn,
  fetchMe,
  invalidateMeCache,
  getInvoice,
} from "../services/authService";
import { getErrorMessage } from "../utils/apiError";
import {
  getPerthTodayStr,
  formatPerthDate,
  formatPerthDateLong,
} from "../utils/perthTime";
import "../style/bookingWebsite.css";

const PAYPAL_CLIENT_ID = process.env.REACT_APP_PAYPAL_CLIENT_ID;

const BookingWebsite = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const selectedBin = searchParams.get("bin");
  const paypalRef = useRef(null);

  const [bins, setBins] = useState([]);
  const [wasteTypes, setWasteTypes] = useState([]);
  const [customer, setCustomer] = useState(null);
  const [authChecked, setAuthChecked] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [loyalty, setLoyalty] = useState(null);
  const [paypalOrderId, setPaypalOrderId] = useState(null);
  const [bookingResult, setBookingResult] = useState(null);
  const [bookingError, setBookingError] = useState("");
  const [step, setStep] = useState(1); // 1 = bin+waste, 2 = date+address, 3 = review
  const [postcodeValid, setPostcodeValid] = useState(false);
  const [postcodeError, setPostcodeError] = useState("");
  const [paypalStep, setPaypalStep] = useState(false); // true when showing paypal
  const [disclaimerChecked, setDisclaimerChecked] = useState(false);
  const [disclaimerShake, setDisclaimerShake] = useState(false);
  const [additionalItems, setAdditionalItems] = useState([]);
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [invoiceData, setInvoiceData] = useState(null);
  const FORM_EXPIRY_MINUTES = 30;

  const [form, setForm] = useState({
    delivery_address: "",
    bin_id: "",
    waste_id: "",
    delivery_date: "",
    postal_code: "",
    hire_weeks: 1,
    additional_items: [],
  });

  const [pricing, setPricing] = useState({
    base_price: 0,
    waste_charge: 0,
    extension_fee: 0,
    booking_fee: 0,
    delivery_charge: 0,
    additional_charge: 0,
    additional_breakdown: [],
    subtotal: 0,
    discount: 0,
    promo_id: null,
    free_bin: false,
    loyalty_discount: 0,
    total: 0,
  });

  useEffect(() => {
    const saved = sessionStorage.getItem("booking_form");
    if (saved) {
      try {
        const { data, savedAt } = JSON.parse(saved);
        const ageMinutes = (Date.now() - savedAt) / 1000 / 60;
        if (ageMinutes < FORM_EXPIRY_MINUTES) {
          setForm((prev) => ({
            ...prev,
            ...data,
            additional_items: data.additional_items || [],
          }));
        } else {
          sessionStorage.removeItem("booking_form");
        }
      } catch (e) {
        sessionStorage.removeItem("booking_form");
      }
    }
  }, []);

  // Save form with timestamp whenever it changes
  useEffect(() => {
    sessionStorage.setItem(
      "booking_form",
      JSON.stringify({
        data: form,
        savedAt: Date.now(),
      }),
    );
  }, [form]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [step]);

  useEffect(() => {
    if (selectedBin) setForm((prev) => ({ ...prev, bin_id: selectedBin }));
  }, [selectedBin]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!isLoggedIn()) {
      sessionStorage.setItem("redirect_after_login", "/booking");
      navigate("/loginpage", { replace: true });
      return;
    }
    fetchMe().then((result) => {
      if (result.success) {
        setCustomer(result.customer);
        setLoyalty(result.loyalty);
      } else {
        sessionStorage.setItem("redirect_after_login", "/booking");
        navigate("/loginpage", { replace: true });
      }
      setAuthChecked(true);
    });
    loadData();
  }, []);

  const fetchPricing = async () => {
    try {
      const res = await api.post("/calculate-price", {
        bin_id: Number(form.bin_id),
        waste_id: form.waste_id ? Number(form.waste_id) : null,
        hire_weeks: Number(form.hire_weeks) || 1,
        postal_code: form.postal_code,
        additional_items: form.additional_items,
      });
      setPricing(res.data);
      if (
        form.postal_code &&
        /^\d{4}$/.test(form.postal_code) &&
        !res.data.postcode_pending
      ) {
        setPostcodeValid(true);
        setPostcodeError("");
      } else {
        setPostcodeValid(false);
      }
    } catch (err) {
      const errMsg = err.response?.data?.error || "Could not calculate price.";
      if (form.postal_code) {
        setPostcodeValid(false);
        setPostcodeError(errMsg);
      }
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!form.bin_id) return;
    const timer = setTimeout(() => fetchPricing(), 300);
    return () => clearTimeout(timer);
  }, [
    form.bin_id,
    form.waste_id,
    form.hire_weeks,
    form.postal_code,
    form.additional_items,
  ]);

  const shakeTimerRef = useRef(null);

  useEffect(() => {
    return () => {
      if (shakeTimerRef.current) clearTimeout(shakeTimerRef.current);
    };
  }, []);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!paypalStep || !paypalOrderId) return;
    let isMounted = true;
    const existing = document.getElementById("paypal-sdk");
    if (existing) existing.remove();
    const script = document.createElement("script");
    script.id = "paypal-sdk";
    script.src = `https://www.paypal.com/sdk/js?client-id=${PAYPAL_CLIENT_ID}&currency=AUD`;
    script.async = true;
    script.onload = () => {
      if (isMounted) renderPayPalButtons();
    };
    document.body.appendChild(script);

    return () => {
      isMounted = false;
      const el = document.getElementById("paypal-sdk");
      if (el) el.remove();
      if (paypalRef.current) paypalRef.current.innerHTML = "";
    };
  }, [paypalStep, paypalOrderId]);

  const loadData = async () => {
    try {
      const binsRes = await api.get("/bins");
      const wasteRes = await api.get("/waste-types");
      const additionalRes = await api.get("/additional-items");
      setBins(binsRes.data);
      setWasteTypes(wasteRes.data);
      setAdditionalItems(additionalRes.data);
    } catch (err) {
      console.log(err);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "postal_code") setPostcodeError("");
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleCompleteBooking = async () => {
    if (!disclaimerChecked) {
      setDisclaimerShake(true);
      if (shakeTimerRef.current) clearTimeout(shakeTimerRef.current);
      shakeTimerRef.current = setTimeout(() => setDisclaimerShake(false), 400);
      return;
    }
    setBookingError("");
    setSubmitting(true);
    try {
      const res = await api.post("/api/payment/create", {
        bin_id: form.bin_id,
        waste_id: form.waste_id,
        hire_weeks: form.hire_weeks,
        delivery_address: form.delivery_address,
        delivery_date: form.delivery_date,
        postal_code: form.postal_code,
        additional_items: form.additional_items,
      });
      const result = res.data || {};
      if (result.success) {
        setPaypalOrderId(result.paypal_order_id);
        setPricing((prev) => ({
          ...prev,
          discount: Number(result.pricing?.discount || 0),
          free_bin: result.pricing?.free_bin || false,
        }));
        setPaypalStep(true);
      } else {
        setBookingError(result.error || "Could not create payment.");
      }
    } catch (err) {
      setBookingError(getErrorMessage(err, "Failed to connect to payment service."));
    } finally {
      setSubmitting(false);
    }
  };

  const renderPayPalButtons = () => {
    if (!window.paypal || !paypalRef.current) return;
    paypalRef.current.innerHTML = "";
    window.paypal
      .Buttons({
        createOrder: () => paypalOrderId,
        onApprove: async (data) => {
          setBookingError("");
          try {
            const res = await api.post("/api/payment/capture", {
              paypal_order_id: data.orderID,
            });
            const result = res.data || {};
            if (result.success) {
              setBookingResult(result);
              invalidateMeCache();
              fetchMe().then((r) => {
                if (r.success) {
                  setCustomer(r.customer);
                  setLoyalty(r.loyalty);
                }
              });
              setPaypalStep(false);
              setStep("success");
            } else {
              setBookingError(result.error || "Payment capture failed.");
            }
          } catch (err) {
            setBookingError(getErrorMessage(err, "Something went wrong confirming payment."));
          }
        },
        onCancel: () => {
          setBookingError("Payment cancelled.");
          setPaypalStep(false);
        },
        onError: (err) => {
          setBookingError("PayPal encountered an error.");
          setPaypalStep(false);
          console.error(err);
        },
        style: {
          layout: "vertical",
          color: "gold",
          shape: "rect",
          label: "pay",
        },
      })
      .render(paypalRef.current);
  };

  const resetForm = () => {
    setStep(1);
    setPaypalStep(false);
    setForm({
      delivery_address: "",
      bin_id: "",
      waste_id: "",
      delivery_date: "",
      postal_code: "",
      hire_weeks: 1,
      additional_items: [],
    });
    setPricing({
      base_price: 0,
      waste_charge: 0,
      extension_fee: 0,
      booking_fee: 0,
      delivery_charge: 0,
      additional_charge: 0,
      additional_breakdown: [],
      subtotal: 0,
      discount: 0,
      promo_id: null,
      free_bin: false,
      loyalty_discount: 0,
      total: 0,
    });
    setPaypalOrderId(null);
    setBookingResult(null);
    setBookingError("");
    setPostcodeValid(false);
    setPostcodeError("");
    setDisclaimerChecked(false);
    setDisclaimerShake(false);
    sessionStorage.removeItem("booking_form");
  };

  const selectedBinData = bins.find(
    (b) => String(b.bin_id) === String(form.bin_id),
  );
  const selectedWasteData = wasteTypes.find(
    (w) => String(w.waste_id) === String(form.waste_id),
  );

  // Step 1 validation
  const step1Valid = form.bin_id && form.waste_id;
  // Step 2 validation
  const step2Valid =
    form.delivery_address && form.delivery_date && postcodeValid;

  if (!authChecked) {
    return (
      <>
        <WebsiteNavbar />
        <div className="bk-page">
          <div className="bk-loading">Loading...</div>
        </div>
        <Footer />
      </>
    );
  }

  const handleOpenSuccessInvoice = async () => {
    if (!bookingResult) return;
    try {
      const inv = await getInvoice(bookingResult.booking_id);
      setInvoiceData(inv || null);
    } catch {
      setInvoiceData(null);
    }
    setShowInvoiceModal(true);
  };

  // SUCCESS
  if (step === "success" && bookingResult) {
    return (
      <>
        <WebsiteNavbar />
        <div className="bk-page">
          <div className="bk-success-wrap">
            <div className="bk-success-card">
              <div className="bk-success-icon">
                <i className="ti ti-circle-check"></i>
              </div>
              <h1>Booking Confirmed!</h1>
              <p>Your payment was successful and your skip bin is booked.</p>
              <div className="bk-success-details">
                <div className="bk-success-row">
                  <span>Booking Reference</span>
                  <strong className="bk-ref">
                    {bookingResult.booking_ref}
                  </strong>
                </div>
                <div className="bk-success-row">
                  <span>Collection Date</span>
                  <strong>{bookingResult.collection_date}</strong>
                </div>
                <div className="bk-success-total">
                  <span>Total Paid</span>
                  <strong>${Number(bookingResult.total_amount || 0).toFixed(2)}</strong>
                </div>
              </div>

              <div className="bk-success-actions">
                <button className="bk-btn-invoice" onClick={handleOpenSuccessInvoice}>
                  🧾 View & Download Tax Invoice
                </button>
                <button className="bk-btn-secondary-link" onClick={() => navigate("/my-account?tab=bookings")}>
                  📦 Go to My Bookings
                </button>
                <button className="bk-btn-primary" onClick={resetForm}>
                  ➕ Book Another Bin
                </button>
              </div>
            </div>
          </div>
        </div>

        {showInvoiceModal && (
          <InvoiceModal
            invoice={invoiceData}
            booking={{
              booking_id: bookingResult.booking_id,
              booking_ref: bookingResult.booking_ref,
              collection_date: bookingResult.collection_date,
              total_amount: bookingResult.total_amount,
              delivery_address: form.delivery_address,
              delivery_date: form.delivery_date,
              bin_size: selectedBinData?.size,
              waste_name: selectedWasteData?.waste_name,
              first_name: customer?.first_name,
              last_name: customer?.last_name,
              email: customer?.email,
              mobile: customer?.mobile,
            }}
            onClose={() => setShowInvoiceModal(false)}
          />
        )}
        <Footer />
      </>
    );
  }

  // Booking Summary sidebar — shown on all 3 steps
  const BookingSummary = () => (
    <div className="bk-summary">
      <div className="bk-summary-header">
        <i className="ti ti-clipboard-list"></i>
        <h3>Booking Summary</h3>
      </div>

      <div className="bk-summary-body">
        <div className="bk-summary-row">
          <span>Selected Bin</span>
          <strong className="bk-summary-orange">
            {selectedBinData ? `${selectedBinData.size} Skip Bin` : "—"}
          </strong>
        </div>
        <div className="bk-summary-row">
          <span>Waste Type</span>
          <strong>{selectedWasteData?.waste_name || "—"}</strong>
        </div>
        <div className="bk-summary-row">
          <span>Hire Duration</span>
          <strong>
            {form.hire_weeks} Week{form.hire_weeks > 1 ? "s" : ""}
          </strong>
        </div>
        {form.delivery_date && (
          <div className="bk-summary-row">
            <span>Delivery Date</span>
            <strong>
              {formatPerthDate(form.delivery_date)}
            </strong>
          </div>
        )}

        <div className="bk-summary-divider" />

        <div className="bk-summary-row">
          <span>Bin Price</span>
          <span>${pricing.base_price?.toFixed(2) || "0.00"}</span>
        </div>
        <div className="bk-summary-row">
          <span>Waste Charge</span>
          <span>${pricing.waste_charge?.toFixed(2) || "0.00"}</span>
        </div>
        {pricing.extension_fee > 0 && (
          <div className="bk-summary-row">
            <span>Extension Fee</span>
            <span>${pricing.extension_fee?.toFixed(2)}</span>
          </div>
        )}
        {pricing.booking_fee > 0 && (
          <div className="bk-summary-row">
            <span>Booking Fee</span>
            <span>${pricing.booking_fee?.toFixed(2)}</span>
          </div>
        )}
        {pricing.delivery_charge > 0 && (
          <div className="bk-summary-row">
            <span>Delivery Charge</span>
            <span>${pricing.delivery_charge?.toFixed(2)}</span>
          </div>
        )}

        {pricing.additional_breakdown?.length > 0 && (
          <>
            <div className="bk-summary-row" style={{ fontWeight: "bold" }}>
              Additional Items
            </div>
            {pricing.additional_breakdown.map((item) => (
              <div
                className="bk-summary-row"
                key={item.item_id}
              >
                <span>
                  {item.item_name} × {item.quantity}
                </span>
                <span>${item.line_total.toFixed(2)}</span>
              </div>
            ))}
          </>
        )}

        {pricing.discount > 0 && (
          <div className="bk-summary-row bk-discount">
            <span>Discount</span>
            <span>-${pricing.discount?.toFixed(2)}</span>
          </div>
        )}

        <div className="bk-summary-total">
          <span>Total</span>
          <strong>${pricing.total?.toFixed(2) || "0.00"}</strong>
        </div>
      </div>

      {loyalty && (
        <div className="bk-loyalty">
          <div className="bk-loyalty-header">
            <i className="ti ti-gift"></i>
            <span>Loyalty Rewards</span>
          </div>
          <p>
            {loyalty.bins_until_free === 7
              ? "6 more bookings until your FREE hire"
              : `${loyalty.bins_until_free} more booking${loyalty.bins_until_free !== 1 ? "s" : ""} until your FREE hire`}
          </p>
          <div className="bk-loyalty-bar">
            <div
              className="bk-loyalty-fill"
              style={{
                width: `${Math.min(((loyalty.progress % 7) / 6) * 100, 100)}%`,
              }}
            />
          </div>
        </div>
      )}

      {pricing?.free_bin && (
        <div className="bk-free-banner">
          <i className="ti ti-gift"></i>
          Loyalty reward applied — FREE bin hire!
        </div>
      )}
    </div>
  );

  // Step progress indicator
  const StepIndicator = () => (
    <div className="bk-steps">
      {[
        {
          n: 1,
          label: "Choose Bin",
          sub: selectedBinData
            ? `${selectedBinData.size} Skip Bin`
            : "Select bin & waste",
        },
        {
          n: 2,
          label: "Delivery Details",
          sub: form.delivery_date
            ? formatPerthDate(form.delivery_date)
            : "Address & date",
        },
        { n: 3, label: "Review & Pay", sub: "Confirm booking" },
      ].map(({ n, label, sub }) => (
        <React.Fragment key={n}>
          <div
            className={`bk-step ${step === n ? "active" : step > n ? "done" : ""}`}
          >
            <div className="bk-step-circle">
              {step > n ? <i className="ti ti-check"></i> : n}
            </div>
            <div className="bk-step-text">
              <span className="bk-step-label">{label}</span>
              <span className="bk-step-sub">{sub}</span>
            </div>
          </div>
          {n < 3 && (
            <div className={`bk-step-line ${step > n ? "done" : ""}`} />
          )}
        </React.Fragment>
      ))}
    </div>
  );

  return (
    <>
      <WebsiteNavbar />
      <div className="bk-page">
        {/* PAGE HEADER */}
        <div className="bk-page-header">
          <div>
            <h1 className="bk-page-title">Book a Bin</h1>
            <p className="bk-page-sub">
              Book the right bin for your project in just a few easy steps.
            </p>
          </div>
          <StepIndicator />
        </div>

        <div className="bk-layout">
          {/* ── MAIN CONTENT ── */}
          <div className="bk-main">
            {bookingError && (
              <div className="bk-error">
                <i className="ti ti-alert-circle"></i> {bookingError}
              </div>
            )}

            {/* ══════════ STEP 1 — Bin + Waste ══════════ */}
            {step === 1 && (
              <div className="bk-card">
                <div className="bk-card-title">
                  <span className="bk-card-num">1</span>
                  Choose Your Bin Size
                </div>
                <p className="bk-card-sub">
                  Select the bin that best suits your project needs.
                </p>

                <div className="bk-bin-list">
                  {bins.map((bin) => (
                    <div
                      key={bin.bin_id}
                      className={`bk-bin-row ${String(form.bin_id) === String(bin.bin_id) ? "active" : ""}`}
                      onClick={() =>
                        setForm((prev) => ({ ...prev, bin_id: bin.bin_id }))
                      }
                    >
                      <div className="bk-bin-icon">
                        <i className="ti ti-trash"></i>
                      </div>
                      <div className="bk-bin-info">
                        <h4>{bin.size} Skip Bin</h4>
                        {/* <p>{bin.description || "Perfect for your project"}</p> */}
                      </div>
                      <div className="bk-bin-price">
                        <strong>${bin.base_price}</strong>
                        <span>Base price</span>
                      </div>
                      <div
                        className={`bk-bin-radio ${String(form.bin_id) === String(bin.bin_id) ? "active" : ""}`}
                      >
                        {String(form.bin_id) === String(bin.bin_id) && (
                          <div className="bk-bin-radio-dot" />
                        )}
                      </div>
                    </div>
                  ))}
                </div>

                <div className="bk-section-divider" />

                <div className="bk-card-title" style={{ marginTop: 0 }}>
                  <span className="bk-card-num">2</span>
                  Waste Type
                </div>
                <p className="bk-card-sub">
                  What type of waste will you be disposing of?
                </p>

                <div className="bk-waste-grid">
                  {wasteTypes.map((waste) => (
                    <div
                      key={waste.waste_id}
                      className={`bk-waste-card bk-tooltip-wrap ${String(form.waste_id) === String(waste.waste_id) ? "active" : ""}`}
                      onClick={() =>
                        setForm((prev) => ({
                          ...prev,
                          waste_id: waste.waste_id,
                        }))
                      }
                    >
                      <i className="ti ti-recycle"></i>
                      <span>{waste.waste_name}</span>

                      {waste.included_items?.length > 0 && (
                        <div className="bk-tooltip">
                          <strong>{waste.waste_name}</strong>
                          {waste.extra_charge > 0 && (
                            <span> (+${waste.extra_charge} per bin)</span>
                          )}
                          <ul>
                            {waste.included_items.map((line, i) => (
                              <li key={i}>{line}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  ))}
                </div>

                <div className="bk-section-divider" />

                <div className="bk-card-title">
                  <span className="bk-card-num">3</span>
                  Additional Items
                </div>
                <p className="bk-card-sub">
                  Have any of these items? Select and set quantity.
                </p>

                <div className="bk-waste-grid">
                  {additionalItems.map((item) => {
                    const selected = form.additional_items.find(
                      (i) => String(i.item_id) === String(item.item_id),
                    );

                    const toggleItem = () => {
                      setForm((prev) => {
                        const exists = prev.additional_items.some(
                          (i) => String(i.item_id) === String(item.item_id),
                        );
                        return {
                          ...prev,
                          additional_items: exists
                            ? prev.additional_items.filter(
                                (i) =>
                                  String(i.item_id) !== String(item.item_id),
                              )
                            : [
                                ...prev.additional_items,
                                { item_id: item.item_id, quantity: 1 },
                              ],
                        };
                      });
                    };

                    const updateQuantity = (e, delta) => {
                      e.stopPropagation(); // prevent the card click (toggleItem) from also firing
                      setForm((prev) => ({
                        ...prev,
                        additional_items: prev.additional_items.map((i) =>
                          String(i.item_id) === String(item.item_id)
                            ? {
                                ...i,
                                quantity: Math.max(1, i.quantity + delta),
                              }
                            : i,
                        ),
                      }));
                    };

                    return (
                      <div
                        key={item.item_id}
                        className={`bk-waste-card ${selected ? "active" : ""}`}
                        onClick={toggleItem}
                      >
                        <i className="ti ti-package"></i>
                        <span>{item.item_name}</span>
                        <span className="bk-item-price">
                          ${item.price} each
                        </span>

                        {selected && (
                          <div
                            className="bk-qty-stepper"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <button
                              type="button"
                              className="bk-qty-btn"
                              onClick={(e) => updateQuantity(e, -1)}
                              aria-label="Decrease quantity"
                            >
                              −
                            </button>
                            <span className="bk-qty-val">{selected.quantity}</span>
                            <button
                              type="button"
                              className="bk-qty-btn"
                              onClick={(e) => updateQuantity(e, 1)}
                              aria-label="Increase quantity"
                            >
                              +
                            </button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>

                <div className="bk-section-divider" />

                <div className="bk-card-title" style={{ marginTop: 24 }}>
                  <span className="bk-card-num">4</span>
                  Hire Duration
                </div>

                <div className="bk-duration-row">
                  {[1, 2, 3].map((week) => (
                    <button
                      key={week}
                      type="button"
                      className={`bk-duration-btn ${form.hire_weeks === week ? "active" : ""}`}
                      onClick={() =>
                        setForm((prev) => ({ ...prev, hire_weeks: week }))
                      }
                    >
                      <i className="ti ti-calendar-week"></i>
                      {week} Week{week > 1 ? "s" : ""}
                    </button>
                  ))}
                </div>

                <div className="bk-card-footer">
                  <button
                    className="bk-btn-primary"
                    disabled={!step1Valid}
                    onClick={() => setStep(2)}
                  >
                    Continue to Delivery Details{" "}
                    <i className="ti ti-arrow-right"></i>
                  </button>
                </div>
              </div>
            )}

            {/* ══════════ STEP 2 — Delivery Details ══════════ */}
            {step === 2 && (
              <div className="bk-card">
                <div className="bk-card-title">
                  <span className="bk-card-num">2</span>
                  Delivery Details
                </div>
                <p className="bk-card-sub">
                  Tell us where and when to deliver your bin.
                </p>

                <div className="bk-field">
                  <label>
                    <i className="ti ti-map-pin"></i> Delivery Address
                  </label>
                  <textarea
                    name="delivery_address"
                    placeholder="Enter your full delivery address"
                    value={form.delivery_address}
                    onChange={handleChange}
                    className="bk-textarea"
                    required
                  />
                </div>

                <div className="bk-two-col">
                  <div className="bk-field">
                    <label>
                      <i className="ti ti-calendar"></i> Delivery Date
                    </label>
                    <input
                      type="date"
                      name="delivery_date"
                      value={form.delivery_date}
                      onChange={handleChange}
                      min={getPerthTodayStr()}
                      className="bk-input"
                      required
                    />
                  </div>

                  <div className="bk-field">
                    <label>
                      <i className="ti ti-map-2"></i> Postal Code
                    </label>
                    <input
                      type="text"
                      name="postal_code"
                      maxLength={4}
                      pattern="[0-9]{4}"
                      inputMode="numeric"
                      placeholder="e.g. 4000"
                      value={form.postal_code}
                      onChange={handleChange}
                      className={`bk-input ${postcodeError ? "error" : ""} ${postcodeValid ? "valid" : ""}`}
                      required
                    />
                    {postcodeError && (
                      <div className="bk-field-error">
                        <i className="ti ti-alert-circle"></i> {postcodeError}
                      </div>
                    )}
                    {postcodeValid && (
                      <div className="bk-field-success">
                        <i className="ti ti-circle-check"></i> Delivery
                        available to this postcode
                      </div>
                    )}
                  </div>
                </div>

                <div className="bk-card-footer bk-footer-split">
                  <button className="bk-btn-back" onClick={() => setStep(1)}>
                    <i className="ti ti-arrow-left"></i> Back
                  </button>
                  <button
                    className="bk-btn-primary"
                    disabled={!step2Valid}
                    onClick={() => setStep(3)}
                  >
                    Review & Pay <i className="ti ti-arrow-right"></i>
                  </button>
                </div>
              </div>
            )}

            {/* ══════════ STEP 3 — Review & Pay ══════════ */}
            {step === 3 && (
              <div className="bk-card">
                <div className="bk-card-title">
                  <span className="bk-card-num">3</span>
                  Review & Confirm Your Booking
                </div>
                <p className="bk-card-sub">
                  Please review your booking details before payment.
                </p>

                {/* Bin review */}
                <div className="bk-review-block">
                  <div className="bk-review-block-header">
                    <div className="bk-review-icon">
                      <i className="ti ti-trash"></i>
                    </div>
                    <div>
                      <strong>Selected Bin</strong>
                      <p>{selectedBinData?.size} Skip Bin</p>
                    </div>
                    <button className="bk-edit-btn" onClick={() => setStep(1)}>
                      <i className="ti ti-edit"></i> Edit
                    </button>
                  </div>
                  <div className="bk-review-chips">
                    <span>
                      <i className="ti ti-recycle"></i>{" "}
                      {selectedWasteData?.waste_name}
                    </span>
                    <span>
                      <i className="ti ti-calendar-week"></i> {form.hire_weeks}{" "}
                      Week{form.hire_weeks > 1 ? "s" : ""}
                    </span>
                    {pricing.additional_breakdown?.map((item) => (
                      <span key={item.item_id}>
                        <i className="ti ti-package"></i> {item.item_name} ×{" "}
                        {item.quantity}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Delivery review */}
                <div className="bk-review-block">
                  <div className="bk-review-block-header">
                    <div className="bk-review-icon">
                      <i className="ti ti-calendar-event"></i>
                    </div>
                    <div>
                      <strong>Delivery Details</strong>
                      <p>
                        {formatPerthDateLong(form.delivery_date, true)}
                      </p>
                    </div>
                    <button className="bk-edit-btn" onClick={() => setStep(2)}>
                      <i className="ti ti-edit"></i> Edit
                    </button>
                  </div>
                </div>

                {/* Address review */}
                <div className="bk-review-block">
                  <div className="bk-review-block-header">
                    <div className="bk-review-icon">
                      <i className="ti ti-map-pin"></i>
                    </div>
                    <div>
                      <strong>Delivery Address</strong>
                      <p>{form.delivery_address}</p>
                      <p style={{ color: "#64748b", fontSize: 13 }}>
                        Postcode: {form.postal_code}
                      </p>
                    </div>
                    <button className="bk-edit-btn" onClick={() => setStep(2)}>
                      <i className="ti ti-edit"></i> Edit
                    </button>
                  </div>
                </div>

                {/* Customer review */}
                {customer && (
                  <div className="bk-review-block">
                    <div className="bk-review-block-header">
                      <div className="bk-review-icon">
                        <i className="ti ti-user"></i>
                      </div>
                      <div>
                        <strong>Contact Details</strong>
                        <p>
                          {customer.full_name
                            ? `${customer.full_name} • ${customer.mobile}`
                            : customer.mobile}
                        </p>
                        <p style={{ color: "#64748b", fontSize: 13 }}>
                          {customer.email}
                        </p>
                      </div>
                    </div>
                  </div>
                )}
                {!paypalStep && (
                  <div
                    className={`bk-disclaimer ${disclaimerShake ? "unchecked" : ""}`}
                  >
                    <div className="bk-disclaimer-text">
                      <p>
                        Our delivery ends at the road verge. If you or a third
                        party request that we enter the property to place a skip
                        bin, all care will be taken however we will not be
                        liable for any damage that may occur to any property and
                        you agree to accept all financial responsibility.
                      </p>
                    </div>
                    <label className="bk-disclaimer-check">
                      <input
                        type="checkbox"
                        checked={disclaimerChecked}
                        onChange={(e) => setDisclaimerChecked(e.target.checked)}
                      />
                      <span>I have read and agree to the disclaimer above</span>
                    </label>
                  </div>
                )}

                {/* PayPal area */}
                {paypalStep ? (
                  <div className="bk-paypal-wrap">
                    <div ref={paypalRef} />
                  </div>
                ) : (
                  <div className="bk-card-footer bk-footer-split">
                    <button className="bk-btn-back" onClick={() => setStep(2)}>
                      <i className="ti ti-arrow-left"></i> Back
                    </button>
                    <button
                      className="bk-btn-primary bk-btn-pay"
                      onClick={handleCompleteBooking}
                      disabled={submitting}
                    >
                      {submitting ? (
                        <>
                          <i className="ti ti-loader"></i> Processing...
                        </>
                      ) : (
                        <>
                          <i className="ti ti-lock"></i> Pay $
                          {pricing.total?.toFixed(2)} Securely
                        </>
                      )}
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* ── SIDEBAR SUMMARY ── */}
          <div className="bk-sidebar">
            <BookingSummary />
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default BookingWebsite;
